import { getFilterOptionsMap, PUBLIC_FILTER_TYPE } from '@/entities/public-filter/public-filter-constants';
import { Button } from '@/shared/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/shared/components/shadcn/dropdown-menu';

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/components/shadcn/command';
import { ScrollArea } from '@/shared/components/shadcn/scroll-area';
import { cn } from '@/shared/utils/index';
import { Check, PlusIcon } from 'lucide-react';
import { getPublicFilterDisplayValue } from './get-public-filter-display-value';

export type FilterMenuProps = {
  values: PublicFilterValue[];
  onChange: (value: PublicFilterValue[]) => void;
};

export type PublicFilterValue = {
  filterType: PUBLIC_FILTER_TYPE;
  value: string | number;
};

const FILTER_OPTIONS_GROUP = [PUBLIC_FILTER_TYPE.PROMOTION_TYPE, PUBLIC_FILTER_TYPE.PLATFORM];

const DEMOGRAPHIC_FILTER_OPTIONS = [
  PUBLIC_FILTER_TYPE.GENDER,
  PUBLIC_FILTER_TYPE.RACE,
  PUBLIC_FILTER_TYPE.AGE,
  PUBLIC_FILTER_TYPE.LOCATION,
];

export function PublicFilterMenu({ values, onChange }: FilterMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <PlusIcon />
          Filter
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        {FILTER_OPTIONS_GROUP.map((filter_option) => (
          <DropdownMenuSub key={filter_option}>
            <DropdownMenuSubTrigger>{filter_option}</DropdownMenuSubTrigger>
            <SubMenuContent filter_option={filter_option} values={values} onChange={onChange} />
          </DropdownMenuSub>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Demographic</DropdownMenuLabel>
        {DEMOGRAPHIC_FILTER_OPTIONS.map((filter_option) => (
          <DropdownMenuSub key={filter_option}>
            <DropdownMenuSubTrigger>{filter_option}</DropdownMenuSubTrigger>
            <SubMenuContent filter_option={filter_option} values={values} onChange={onChange} />
          </DropdownMenuSub>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const SubMenuContent = ({
  filter_option,
  values,
  onChange,
}: {
  filter_option: PUBLIC_FILTER_TYPE;
  values: PublicFilterValue[];
  onChange: (value: PublicFilterValue[]) => void;
}) => {
  return (
    <DropdownMenuSubContent>
      <Command>
        <CommandInput placeholder={`Search ${filter_option}`} />
        <CommandList>
          <ScrollArea className="h-70 w-60 pt-1">
            <CommandGroup>
              {getFilterOptionsMap(filter_option)?.map((option_value) => {
                const isChecked = values.some(
                  (v) => v.filterType === filter_option && v.value === option_value,
                );
                const { label } = getPublicFilterDisplayValue(filter_option, option_value);
                return (
                  <CommandItem
                    key={`${filter_option}-${option_value}`}
                    onSelect={() => {
                      if (!isChecked) {
                        onChange([...values, { filterType: filter_option, value: option_value }]);
                      } else {
                        onChange(
                          values.filter(
                            (v) => !(v.filterType === filter_option && v.value === option_value),
                          ),
                        );
                      }
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Check className={cn('ml-auto', isChecked ? 'opacity-100' : 'opacity-0')} />
                      {label}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </ScrollArea>
        </CommandList>
      </Command>
    </DropdownMenuSubContent>
  );
};
